// src/components/FormModal.jsx
import React, {useEffect, useRef} from 'react'
import clsx from 'clsx'

/**
 * Reusable Bootstrap modal (no jQuery).
 * Props:
 *  - open (bool)
 *  - onClose (func)
 *  - title (node|string)
 *  - children (ReactElement)  // will receive { onSuccess } if valid element
 *  - onSuccess (func)         // injected into child form
 *  - size: 'sm'|'lg'|'xl'     // Bootstrap modal sizes
 *  - centered (bool)
 *  - scrollable (bool)
 *  - staticBackdrop (bool)    // disables close on backdrop/Esc
 *  - dialogClassName, contentClassName, bodyClassName (string)
 *  - zIndex (number)          // default 1050
 *  - removeBodyPadding (bool) // set modal-body padding to 0 via bodyClassName="p-0"
 */
export default function FormModal({
                                      open,
                                      onClose,
                                      title,
                                      children,
                                      onSuccess,
                                      isDialogModal,
                                      size,
                                      centered,
                                      scrollable,
                                      staticBackdrop,
                                      dialogClassName,
                                      contentClassName,
                                      bodyClassName,
                                      zIndex = 1050,
                                  }) {
    const backdropRef = useRef(null)

    // Lock body scroll like Bootstrap does
    useEffect(() => {
        if (!open) return
        const prevOverflow = document.body.style.overflow
        document.body.classList.add('modal-open')
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.classList.remove('modal-open')
            document.body.style.overflow = prevOverflow
        }
    }, [open])

    // Close on ESC (unless static)
    useEffect(() => {
        if (!open || staticBackdrop) return
        const onKey = (e) => {
            if (e.key === 'Escape') onClose?.()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open, staticBackdrop, onClose])

    const handleBackdropClick = (e) => {
        if (staticBackdrop) return
        if (e.target === backdropRef.current) onClose?.()
    }

    const child = React.isValidElement(children)
        ? React.cloneElement(children, {onSuccess, isDialogModal})
        : children

    const sizeClass =
        size === 'sm' ? 'modal-sm' :
            size === 'lg' ? 'modal-lg' :
                size === 'xl' ? 'modal-xl' : undefined

    if (!open) return null

    return (
        <div
            ref={backdropRef}
            className={clsx('modal', 'fade', 'show')}
            role="dialog"
            aria-modal="true"
            style={{display: 'block', background: 'rgba(0,0,0,0.5)', zIndex}}
            onMouseDown={handleBackdropClick}
        >
            <div
                className={clsx(
                    'modal-dialog',
                    sizeClass,
                    centered && 'modal-dialog-centered',
                    scrollable && 'modal-dialog-scrollable',
                    dialogClassName,
                )}
                role="document"
                onMouseDown={(e) => e.stopPropagation()} // prevent backdrop close on content click
            >
                <div className={clsx('modal-content', contentClassName)}>
                    <div className="modal-header">
                        <h5 className="modal-title">{title}</h5>
                        <button type="button" className="btn-close" onClick={() => onClose?.()}/>
                    </div>
                    <div className={clsx('modal-body', bodyClassName)}>
                        {child}
                    </div>
                </div>
            </div>
        </div>
    )
}
